import { useCallback, useEffect, useRef, useState } from "react";
import type {
  ServerMessage,
  ClientMessage,
  PlaybackSpeed,
} from "../server/types";

type MessageHandler = (msg: ServerMessage) => void;

// Singleton WebSocket to prevent StrictMode double-connection
let globalWs: WebSocket | null = null;
let globalHandlers = new Set<MessageHandler>();
let connectionCount = 0;

export function useWebSocket() {
  const [connected, setConnected] = useState(false);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const connect = useCallback(() => {
    if (globalWs && (globalWs.readyState === WebSocket.OPEN || globalWs.readyState === WebSocket.CONNECTING)) {
      if (globalWs.readyState === WebSocket.OPEN) setConnected(true);
      return;
    }

    const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const ws = new WebSocket(`${protocol}//${window.location.host}/ws`);

    ws.onopen = () => {
      setConnected(true);
      console.log("[WS] Connected");
    };

    ws.onclose = () => {
      setConnected(false);
      if (globalWs === ws) globalWs = null;
      // Auto-reconnect after 2s only if we still have subscribers
      if (connectionCount > 0) {
        reconnectTimeoutRef.current = setTimeout(connect, 2000);
      }
    };

    ws.onerror = () => {
      ws.close();
    };

    ws.onmessage = (event) => {
      try {
        const msg: ServerMessage = JSON.parse(event.data);
        for (const handler of globalHandlers) {
          handler(msg);
        }
      } catch (e) {
        console.error("[WS] Failed to parse message:", e);
      }
    };

    globalWs = ws;
  }, []);

  useEffect(() => {
    connectionCount++;
    connect();
    return () => {
      connectionCount--;
      clearTimeout(reconnectTimeoutRef.current);
      if (connectionCount === 0) {
        globalWs?.close();
        globalWs = null;
      }
    };
  }, [connect]);

  const send = useCallback((msg: ClientMessage) => {
    if (globalWs?.readyState === WebSocket.OPEN) {
      globalWs.send(JSON.stringify(msg));
    }
  }, []);

  const addHandler = useCallback((handler: MessageHandler) => {
    globalHandlers.add(handler);
    return () => {
      globalHandlers.delete(handler);
    };
  }, []);

  const sendEmulatorReady = useCallback(() => {
    send({ type: "emulator_ready" });
  }, [send]);

  const sendCommandResult = useCallback(
    (id: string, result: { screenshot?: string; totalFrames: number; success: boolean; error?: string }) => {
      send({ type: "command_result", id, result });
    },
    [send]
  );

  const sendUserAction = useCallback(
    (action: "start" | "stop" | "change_model" | "change_speed" | "get_game_prompt" | "set_game_prompt" | "request_replay" | "set_api_key" | "clear_api_key", params?: { model?: string; speed?: PlaybackSpeed; gamePrompt?: string; apiKey?: string }) => {
      send({ type: "user_action", action, params });
    },
    [send]
  );

  return {
    connected,
    send,
    addHandler,
    sendEmulatorReady,
    sendCommandResult,
    sendUserAction,
  };
}
